import type { ToolPageMode } from './prebuilt-tools.ts';
import type { FlowViewType } from './views.ts';

export const TOOL_SURFACE_SPEC_VERSION = '1.0' as const;

export const TOOL_SURFACE_WORKSPACE_TAB_IDS = ['tasks', 'library', 'team'] as const;
export const TOOL_SURFACE_PREVIEW_TAB_IDS = ['result', 'library', 'team'] as const;
export const TOOL_SURFACE_SECTION_IDS = ['artifact', 'summary', 'actions', 'context', 'detail'] as const;

export type ToolSurfaceWorkspaceTabId = typeof TOOL_SURFACE_WORKSPACE_TAB_IDS[number];
export type ToolSurfacePreviewTabId = typeof TOOL_SURFACE_PREVIEW_TAB_IDS[number];
export type ToolSurfaceSectionId = typeof TOOL_SURFACE_SECTION_IDS[number];

export interface ToolSurfaceWorkspaceTab {
  id: ToolSurfaceWorkspaceTabId;
  label: string;
}

export interface ToolSurfacePreviewTab {
  id: ToolSurfacePreviewTabId;
  label: string;
}

export interface ToolSurfaceSpec {
  version: typeof TOOL_SURFACE_SPEC_VERSION;
  workspace: {
    defaultTab: 'tasks';
    tabs: ToolSurfaceWorkspaceTab[];
  };
  preview: {
    defaultTab: 'result';
    tabs: ToolSurfacePreviewTab[];
  };
  result: {
    pageMode: ToolPageMode;
    sectionOrder: ToolSurfaceSectionId[];
  };
  rationale?: string;
}

const PAGE_MODE_BY_VIEW_TYPE: Record<FlowViewType, ToolPageMode> = {
  action_list: 'diagnostic',
  artifact_document: 'artifact',
  data_table: 'data',
  entity_summary: 'diagnostic',
  health_gauge: 'data',
  metric_grid: 'data',
  chart: 'data',
  timeline: 'data',
  score_card: 'diagnostic',
};

const SECTION_ORDER_BY_VIEW_TYPE: Record<FlowViewType, ToolSurfaceSectionId[]> = {
  action_list: ['actions', 'summary', 'context', 'detail'],
  artifact_document: ['artifact', 'actions', 'context'],
  data_table: ['detail', 'summary', 'actions', 'context'],
  entity_summary: ['context', 'summary', 'actions', 'detail'],
  health_gauge: ['summary', 'detail', 'actions', 'context'],
  metric_grid: ['summary', 'detail', 'actions', 'context'],
  chart: ['summary', 'detail', 'actions', 'context'],
  timeline: ['summary', 'detail', 'actions', 'context'],
  score_card: ['summary', 'actions', 'context', 'detail'],
};

const PRIMARY_TAB_LABEL_BY_VIEW_TYPE: Record<FlowViewType, string> = {
  action_list: 'Actions',
  artifact_document: 'Documents',
  data_table: 'Tableau',
  entity_summary: 'Fiches',
  health_gauge: 'Analyses',
  metric_grid: 'Analyses',
  chart: 'Graphiques',
  timeline: 'Chronologie',
  score_card: 'Diagnostics',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isWorkspaceTabId = (value: unknown): value is ToolSurfaceWorkspaceTabId =>
  typeof value === 'string' &&
  TOOL_SURFACE_WORKSPACE_TAB_IDS.includes(value as ToolSurfaceWorkspaceTabId);

const isPreviewTabId = (value: unknown): value is ToolSurfacePreviewTabId =>
  typeof value === 'string' &&
  TOOL_SURFACE_PREVIEW_TAB_IDS.includes(value as ToolSurfacePreviewTabId);

const isSectionId = (value: unknown): value is ToolSurfaceSectionId =>
  typeof value === 'string' &&
  TOOL_SURFACE_SECTION_IDS.includes(value as ToolSurfaceSectionId);

const isPageMode = (value: unknown): value is ToolPageMode =>
  value === 'diagnostic' || value === 'artifact' || value === 'data';

const cleanLabel = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.replace(/\s+/g, ' ').trim();
  return trimmed.length > 0 ? trimmed.slice(0, 36) : fallback;
};

const getWorkspaceTabFallbackLabel = (
  id: ToolSurfaceWorkspaceTabId,
  viewType: FlowViewType,
): string => {
  if (id === 'tasks') return PRIMARY_TAB_LABEL_BY_VIEW_TYPE[viewType];
  if (id === 'library') return 'Bibliothèque';
  return 'Équipe';
};

const getPreviewTabFallbackLabel = (id: ToolSurfacePreviewTabId): string => {
  if (id === 'result') return 'Résultat';
  if (id === 'library') return 'Bibliothèque';
  return 'Équipe';
};

const hasWorkspaceTab = (
  tabs: ToolSurfaceWorkspaceTab[],
  id: ToolSurfaceWorkspaceTabId,
): boolean => tabs.some((tab) => tab.id === id);

const normalizeWorkspaceTabs = (
  value: unknown,
  viewType: FlowViewType,
): ToolSurfaceWorkspaceTab[] => {
  const tabs: ToolSurfaceWorkspaceTab[] = [];
  const source = Array.isArray(value) ? value : [];

  for (const item of source) {
    if (!isRecord(item) || !isWorkspaceTabId(item.id) || item.enabled === false) continue;
    if (tabs.some((tab) => tab.id === item.id)) continue;
    tabs.push({
      id: item.id,
      label: cleanLabel(item.label, getWorkspaceTabFallbackLabel(item.id, viewType)),
    });
  }

  if (!hasWorkspaceTab(tabs, 'tasks')) {
    tabs.unshift({ id: 'tasks', label: PRIMARY_TAB_LABEL_BY_VIEW_TYPE[viewType] });
  }

  return tabs;
};

const normalizePreviewTabs = (
  value: unknown,
  workspaceTabs: ToolSurfaceWorkspaceTab[],
): ToolSurfacePreviewTab[] => {
  const tabs: ToolSurfacePreviewTab[] = [];
  const source = Array.isArray(value) ? value : [];

  for (const item of source) {
    if (!isRecord(item) || !isPreviewTabId(item.id) || item.enabled === false) continue;
    if (item.id === 'library' && !hasWorkspaceTab(workspaceTabs, 'library')) continue;
    if (item.id === 'team' && !hasWorkspaceTab(workspaceTabs, 'team')) continue;
    if (tabs.some((tab) => tab.id === item.id)) continue;
    tabs.push({
      id: item.id,
      label: cleanLabel(item.label, getPreviewTabFallbackLabel(item.id)),
    });
  }

  if (!tabs.some((tab) => tab.id === 'result')) {
    tabs.unshift({ id: 'result', label: 'Résultat' });
  }

  return tabs;
};

const normalizeSectionOrder = (
  value: unknown,
  viewType: FlowViewType,
): ToolSurfaceSectionId[] => {
  const source = Array.isArray(value) ? value : [];
  const sections = source.filter(isSectionId);
  return sections.length > 0 ? [...new Set(sections)] : SECTION_ORDER_BY_VIEW_TYPE[viewType];
};

export const getDefaultToolSurfaceSpec = (params: {
  viewType: FlowViewType;
}): ToolSurfaceSpec => ({
  version: TOOL_SURFACE_SPEC_VERSION,
  workspace: {
    defaultTab: 'tasks',
    tabs: [
      {
        id: 'tasks',
        label: PRIMARY_TAB_LABEL_BY_VIEW_TYPE[params.viewType],
      },
    ],
  },
  preview: {
    defaultTab: 'result',
    tabs: [
      {
        id: 'result',
        label: 'Résultat',
      },
    ],
  },
  result: {
    pageMode: PAGE_MODE_BY_VIEW_TYPE[params.viewType],
    sectionOrder: SECTION_ORDER_BY_VIEW_TYPE[params.viewType],
  },
});

export const normalizeToolSurfaceSpec = (
  value: unknown,
  params: {
    viewType: FlowViewType;
  },
): ToolSurfaceSpec => {
  const fallback = getDefaultToolSurfaceSpec(params);
  if (!isRecord(value)) return fallback;

  const workspace = isRecord(value.workspace) ? value.workspace : {};
  const workspaceTabs = normalizeWorkspaceTabs(workspace.tabs, params.viewType);
  const preview = isRecord(value.preview) ? value.preview : {};
  const previewTabs = normalizePreviewTabs(preview.tabs, workspaceTabs);
  const result = isRecord(value.result) ? value.result : {};

  return {
    version: TOOL_SURFACE_SPEC_VERSION,
    workspace: {
      defaultTab: 'tasks',
      tabs: workspaceTabs,
    },
    preview: {
      defaultTab: 'result',
      tabs: previewTabs,
    },
    result: {
      pageMode: isPageMode(result.pageMode)
        ? result.pageMode
        : fallback.result.pageMode,
      sectionOrder: normalizeSectionOrder(result.sectionOrder, params.viewType),
    },
    ...(typeof value.rationale === 'string' && value.rationale.trim().length > 0
      ? { rationale: value.rationale.trim().slice(0, 300) }
      : {}),
  };
};
